import { useEffect, useState } from "react";
import api from "../lib/api";
import { formatIDR, formatDate, todayISO } from "../lib/format";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import { Plus, Trash2, PackageCheck, CheckCircle2, XCircle, ShieldAlert, Printer } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { generatePurchaseOrderPDF } from "../lib/poPdf";
import PdfPreviewDialog from "../components/PdfPreviewDialog";

const STATUS_STYLE = {
  draft: "bg-slate-100 text-slate-700 border-slate-200",
  ordered: "bg-blue-100 text-blue-700 border-blue-200",
  pending_approval: "bg-yellow-100 text-yellow-800 border-yellow-200",
  approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
  rejected: "bg-red-100 text-red-700 border-red-200",
  received: "bg-emerald-100 text-emerald-700 border-emerald-200",
  cancelled: "bg-red-100 text-red-700 border-red-200",
};
const STATUS_LABEL = {
  draft: "DRAFT", ordered: "ORDERED", pending_approval: "MENUNGGU APPROVAL",
  approved: "APPROVED", rejected: "DITOLAK", received: "DITERIMA", cancelled: "DIBATALKAN",
};

export default function Purchasing() {
  const { user } = useAuth();
  const isAdmin = user.role === "admin";
  const [pos, setPos] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [products, setProducts] = useState([]);
  const [company, setCompany] = useState({});
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ supplier_id: "", supplier_name: "", order_date: todayISO(), notes: "", items: [], status: "draft", include_ppn: false, ppn_percent: 11, po_discount_type: "none", po_discount_value: 0, payment_term: "top_30", pph23_enabled: false, pph23_percent: 2, payment_schedule_preset: "single" });
  const [rejectPO, setRejectPO] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [pdfPrev, setPdfPrev] = useState({ open: false, url: "", filename: "", title: "" });

  const load = () => Promise.all([
    api.get("/purchase-orders").then((r) => setPos(r.data)),
    api.get("/suppliers").then((r) => setSuppliers(r.data)),
    api.get("/products").then((r) => setProducts(r.data)),
    api.get("/company-profile").then((r) => {
      setCompany(r.data);
      setForm((f) => ({ ...f, ppn_percent: r.data.default_ppn_percent ?? 11 }));
    }).catch(() => {}),
  ]);
  useEffect(() => { load(); }, []);

  const threshold = Number(company?.po_approval_threshold || 0);

  const addItem = () => {
    // Baris kosong — user dapat isi manual, atau pilih produk dari inventory (opsional)
    setForm({ ...form, items: [...form.items, { product_id: "", sku: "", name: "", quantity: 1, price: 0, discount_type: "none", discount_value: 0 }] });
  };
  const updateItem = (i, field, value) => {
    const items = [...form.items];
    if (field === "product_id") {
      if (value === "__manual__" || !value) {
        items[i] = { ...items[i], product_id: "" };
      } else {
        const p = products.find((x) => x.id === value);
        if (p) items[i] = { ...items[i], product_id: p.id, sku: p.sku, name: p.name, price: p.purchase_price };
      }
    } else if (field === "discount_type") {
      items[i] = { ...items[i], discount_type: value, discount_value: value === "none" ? 0 : items[i].discount_value };
    } else items[i] = { ...items[i], [field]: field === "quantity" || field === "price" || field === "discount_value" ? Number(value) : value };
    setForm({ ...form, items });
  };
  const removeItem = (i) => setForm({ ...form, items: form.items.filter((_, idx) => idx !== i) });

  const calcLineDiscount = (it) => {
    const gross = (it.quantity || 0) * (it.price || 0);
    if (it.discount_type === "percent") return Math.min(gross, gross * (Number(it.discount_value) || 0) / 100);
    if (it.discount_type === "amount") return Math.min(gross, Number(it.discount_value) || 0);
    return 0;
  };
  const grossSubtotal = form.items.reduce((s, it) => s + (it.quantity || 0) * (it.price || 0), 0);
  const itemDiscountTotal = form.items.reduce((s, it) => s + calcLineDiscount(it), 0);
  const subtotalAfterItem = grossSubtotal - itemDiscountTotal;
  const poDiscount = form.po_discount_type === "percent"
    ? Math.min(subtotalAfterItem, subtotalAfterItem * (Number(form.po_discount_value) || 0) / 100)
    : form.po_discount_type === "amount"
      ? Math.min(subtotalAfterItem, Number(form.po_discount_value) || 0)
      : 0;
  const netSubtotal = subtotalAfterItem - poDiscount;
  const ppn = form.include_ppn ? netSubtotal * (Number(form.ppn_percent) || 0) / 100 : 0;
  const total = netSubtotal + ppn;
  const willRequireApproval = threshold > 0 && total >= threshold;

  const save = async () => {
    if (!form.supplier_id) return toast.error("Pilih supplier");
    if (form.items.length === 0) return toast.error("Tambahkan minimal 1 item");
    for (const it of form.items) {
      if (!it.name?.trim()) return toast.error("Setiap item wajib punya nama");
      if (!(Number(it.quantity) > 0)) return toast.error("Qty item harus > 0");
    }
    try {
      // Bangun payment_schedule bila preset != single
      const scheduleMap = {
        single: null,
        "30_70": [{ percent: 30, description: "DP", term_days: 0 }, { percent: 70, description: "Pelunasan", term_days: 30 }],
        "50_50": [{ percent: 50, description: "DP", term_days: 0 }, { percent: 50, description: "Pelunasan", term_days: 30 }],
        "30_40_30": [{ percent: 30, description: "DP", term_days: 0 }, { percent: 40, description: "Progress", term_days: 14 }, { percent: 30, description: "Pelunasan", term_days: 30 }],
      };
      const payload = {
        ...form,
        po_discount_type: form.po_discount_type === "none" ? null : form.po_discount_type,
        po_discount_value: form.po_discount_type === "none" ? 0 : Number(form.po_discount_value) || 0,
        payment_schedule: scheduleMap[form.payment_schedule_preset] || null,
        items: form.items.map((it) => ({
          ...it,
          product_id: it.product_id || null,
          sku: it.sku || "",
          is_service: !!it.is_service,
          discount_type: it.discount_type === "none" ? null : it.discount_type,
          discount_value: it.discount_type === "none" ? 0 : Number(it.discount_value) || 0,
        })),
      };
      await api.post("/purchase-orders", payload);
      toast.success(willRequireApproval && form.status !== "draft" ? "PO dibuat — menunggu approval Owner" : "PO dibuat");
      setOpen(false);
      setForm({ supplier_id: "", supplier_name: "", order_date: todayISO(), notes: "", items: [], status: "draft", include_ppn: false, ppn_percent: company.default_ppn_percent ?? 11, po_discount_type: "none", po_discount_value: 0, payment_term: "top_30", pph23_enabled: false, pph23_percent: 2, payment_schedule_preset: "single" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };

  const receive = async (id) => {
    if (!window.confirm("Tandai PO sebagai DITERIMA? Stok bertambah otomatis & utang tercatat.")) return;
    try { await api.post(`/purchase-orders/${id}/receive`); toast.success("PO diterima"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };

  const setStatus = async (id, status) => {
    try { await api.put(`/purchase-orders/${id}/status`, { status }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };

  const approve = async (id) => {
    if (!window.confirm("Approve PO ini? Staff dapat menerima barang setelahnya.")) return;
    try { await api.post(`/purchase-orders/${id}/approve`); toast.success("PO disetujui"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };

  const submitReject = async () => {
    if (!rejectReason.trim()) return toast.error("Alasan wajib diisi");
    try {
      await api.post(`/purchase-orders/${rejectPO.id}/reject`, { reason: rejectReason });
      toast.success("PO ditolak"); setRejectPO(null); setRejectReason(""); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };

  const printPO = (po) => {
    const sup = suppliers.find((s) => s.id === po.supplier_id) || {};
    try {
      const res = generatePurchaseOrderPDF({ po, supplier: sup, company, preview: true });
      if (res?.url) setPdfPrev({ open: true, url: res.url, filename: res.filename, title: `Preview PO ${po.po_number}` });
    } catch (e) { toast.error("Gagal cetak PO: " + e.message); }
  };

  const pendingCount = pos.filter((p) => p.status === "pending_approval").length;

  return (
    <div className="space-y-6" data-testid="purchasing-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[11px] font-medium uppercase tracking-widest text-slate-500">Modul Pembelian</div>
          <h1 className="font-display text-3xl font-extrabold text-slate-900 tracking-tight">Purchase Orders</h1>
          <p className="text-sm text-slate-500 mt-1">Saat status "Received" — stok & utang otomatis diperbarui.{threshold > 0 && ` PO ≥ ${formatIDR(threshold)} butuh approval Owner.`}</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="bg-navy-900 hover:bg-navy-800 text-white" data-testid="po-new-btn"><Plus size={16} className="mr-2" />Buat PO Baru</Button></DialogTrigger>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-display">Purchase Order Baru</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Supplier</Label>
                <Select value={form.supplier_id} onValueChange={(v) => { const s = suppliers.find((x) => x.id === v); setForm({ ...form, supplier_id: v, supplier_name: s?.name || "" }); }}>
                  <SelectTrigger data-testid="po-supplier-select"><SelectValue placeholder="Pilih supplier" /></SelectTrigger>
                  <SelectContent>{suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Tanggal</Label><Input type="date" value={form.order_date} onChange={(e) => setForm({ ...form, order_date: e.target.value })} /></div>
              <div className="col-span-2">
                <Label>Status Awal</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="ordered">Ordered</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2 flex items-center justify-between rounded-lg border border-slate-200 p-3 bg-slate-50">
                <div className="flex-1">
                  <Label className="cursor-pointer">Kenakan PPN</Label>
                  <div className="text-xs text-slate-500">Aktifkan bila supplier PKP</div>
                </div>
                <div className="flex items-center gap-3">
                  <Select value={String(form.ppn_percent)} onValueChange={(v) => setForm({ ...form, ppn_percent: Number(v) })} disabled={!form.include_ppn}>
                    <SelectTrigger className="w-24 h-9" data-testid="po-ppn-rate"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="11">11%</SelectItem>
                      <SelectItem value="12">12%</SelectItem>
                      <SelectItem value="13">13%</SelectItem>
                      <SelectItem value="14">14%</SelectItem>
                      <SelectItem value="15">15%</SelectItem>
                    </SelectContent>
                  </Select>
                  <Switch checked={form.include_ppn} onCheckedChange={(v) => setForm({ ...form, include_ppn: v })} data-testid="po-ppn-toggle" />
                </div>
              </div>
              <div className="col-span-2"><Label>Catatan</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
              <div className="col-span-2">
                <Label>Rencana Pembayaran *</Label>
                <Select value={form.payment_term} onValueChange={(v) => setForm({ ...form, payment_term: v })}>
                  <SelectTrigger data-testid="po-payment-term"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cash">Cash / Transfer (Bayar Langsung)</SelectItem>
                    <SelectItem value="top_7">TOP 7 hari</SelectItem>
                    <SelectItem value="top_14">TOP 14 hari</SelectItem>
                    <SelectItem value="top_30">TOP 30 hari</SelectItem>
                    <SelectItem value="top_45">TOP 45 hari</SelectItem>
                    <SelectItem value="top_60">TOP 60 hari</SelectItem>
                  </SelectContent>
                </Select>
                <div className="text-[11px] text-slate-500 mt-1">
                  {form.payment_term === "cash"
                    ? "Saat PO diterima: langsung Kredit Kas & catat Cash Flow keluar (tanpa AP)"
                    : `Saat PO diterima: catat Utang Dagang dengan jatuh tempo ${{ top_7: 7, top_14: 14, top_30: 30, top_45: 45, top_60: 60 }[form.payment_term]} hari`}
                </div>
              </div>

              {/* PPh 23 */}
              <div className="col-span-2 flex items-center justify-between rounded-lg border border-slate-200 p-3 bg-slate-50">
                <div className="flex-1">
                  <Label>Potong PPh Pasal 23 (Jasa)</Label>
                  <div className="text-xs text-slate-500">Aktif bila PO ini mengandung item jasa (tandai "Jasa" per item)</div>
                </div>
                <div className="flex items-center gap-3">
                  <Select value={String(form.pph23_percent)} onValueChange={(v) => setForm({ ...form, pph23_percent: Number(v) })} disabled={!form.pph23_enabled}>
                    <SelectTrigger className="w-24 h-9" data-testid="po-pph23-rate"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="2">2% (NPWP)</SelectItem>
                      <SelectItem value="4">4% (Non-NPWP)</SelectItem>
                    </SelectContent>
                  </Select>
                  <Switch checked={form.pph23_enabled} onCheckedChange={(v) => setForm({ ...form, pph23_enabled: v })} data-testid="po-pph23-toggle" />
                </div>
              </div>

              {/* Skema Cicilan */}
              <div className="col-span-2">
                <Label>Skema Pembayaran</Label>
                <Select value={form.payment_schedule_preset} onValueChange={(v) => setForm({ ...form, payment_schedule_preset: v })}>
                  <SelectTrigger data-testid="po-schedule-preset"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="single">Sekali Bayar (sesuai TOP di atas)</SelectItem>
                    <SelectItem value="30_70">Cicilan 30% DP + 70% (setelah 30 hari)</SelectItem>
                    <SelectItem value="50_50">Cicilan 50% DP + 50% (setelah 30 hari)</SelectItem>
                    <SelectItem value="30_40_30">30% DP + 40% (H+14) + 30% (H+30)</SelectItem>
                  </SelectContent>
                </Select>
                <div className="text-[11px] text-slate-500 mt-1">Cicilan membuat multiple AP entries dengan due date berbeda saat PO diterima.</div>
              </div>
            </div>
            <div className="mt-4">
              <div className="flex justify-between mb-2"><Label>Item Barang</Label><Button size="sm" variant="outline" onClick={addItem} data-testid="po-add-item"><Plus size={14} className="mr-1" />Tambah Item</Button></div>
              <div className="space-y-2">
                {form.items.map((it, i) => {
                  const lineDisc = calcLineDiscount(it);
                  const lineNet = (it.quantity || 0) * (it.price || 0) - lineDisc;
                  return (
                    <div key={i} className="grid grid-cols-12 gap-2 items-end bg-slate-50 p-2 rounded-lg">
                      <div className="col-span-12 md:col-span-4">
                        <Label className="text-[10px] text-slate-500">Produk (opsional — dari inventory)</Label>
                        <Select value={it.product_id || "__manual__"} onValueChange={(v) => updateItem(i, "product_id", v)}>
                          <SelectTrigger data-testid={`po-item-product-${i}`}><SelectValue placeholder="— Ketik manual atau pilih —" /></SelectTrigger>
                          <SelectContent className="max-h-72">
                            <SelectItem value="__manual__">— Item Manual (ketik sendiri) —</SelectItem>
                            {products.map((p) => <SelectItem key={p.id} value={p.id}>{p.sku} — {p.name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="col-span-6 md:col-span-2">
                        <Label className="text-[10px] text-slate-500">SKU {it.product_id && <span className="text-emerald-600">• linked</span>}</Label>
                        <Input value={it.sku || ""} onChange={(e) => updateItem(i, "sku", e.target.value)} placeholder="Opsional" data-testid={`po-item-sku-${i}`} />
                      </div>
                      <div className="col-span-6 md:col-span-6">
                        <Label className="text-[10px] text-slate-500">Nama Barang / Jasa *</Label>
                        <Input value={it.name || ""} onChange={(e) => updateItem(i, "name", e.target.value)} placeholder="Cth: Semen Portland 50kg / Jasa Instalasi" data-testid={`po-item-name-${i}`} />
                      </div>
                      <div className="col-span-3 md:col-span-2">
                        <Label className="text-[10px] text-slate-500">Qty</Label>
                        <Input type="number" value={it.quantity} onChange={(e) => updateItem(i, "quantity", e.target.value)} data-testid={`po-item-qty-${i}`} />
                      </div>
                      <div className="col-span-8 md:col-span-4">
                        <Label className="text-[10px] text-slate-500">Harga Satuan</Label>
                        <Input type="number" value={it.price} onChange={(e) => updateItem(i, "price", e.target.value)} data-testid={`po-item-price-${i}`} />
                      </div>
                      <div className="col-span-1 flex justify-end flex-col items-end gap-1">
                        <label className="flex items-center gap-1 text-[10px] text-slate-500 cursor-pointer">
                          <input type="checkbox" checked={!!it.is_service} onChange={(e) => updateItem(i, "is_service", e.target.checked)} data-testid={`po-item-service-${i}`} />
                          Jasa
                        </label>
                        <Button size="sm" variant="ghost" onClick={() => removeItem(i)}><Trash2 size={14} className="text-red-500" /></Button>
                      </div>
                      <div className="col-span-4 md:col-span-3">
                        <Label className="text-[10px] text-slate-500">Tipe Diskon</Label>
                        <Select value={it.discount_type || "none"} onValueChange={(v) => updateItem(i, "discount_type", v)}>
                          <SelectTrigger data-testid={`po-item-disc-type-${i}`}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">Tanpa Diskon</SelectItem>
                            <SelectItem value="percent">Persen (%)</SelectItem>
                            <SelectItem value="amount">Nominal (Rp)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="col-span-4 md:col-span-3">
                        <Label className="text-[10px] text-slate-500">
                          Nilai Diskon {it.discount_type === "percent" ? "(%)" : it.discount_type === "amount" ? "(Rp)" : ""}
                        </Label>
                        <Input
                          type="number"
                          value={it.discount_value || 0}
                          disabled={!it.discount_type || it.discount_type === "none"}
                          onChange={(e) => updateItem(i, "discount_value", e.target.value)}
                          data-testid={`po-item-disc-value-${i}`}
                        />
                      </div>
                      <div className="col-span-4 md:col-span-5">
                        <Label className="text-[10px] text-slate-500">Subtotal Net</Label>
                        <div className="h-9 rounded-md border border-slate-200 bg-white px-3 flex items-center justify-end font-mono text-sm text-navy-900">
                          {formatIDR(lineNet)}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="mt-3 flex flex-col md:flex-row md:justify-end gap-4">
                <div className="w-full md:w-72 space-y-2 bg-slate-50 border border-slate-200 rounded-lg p-3">
                  <Label className="text-xs font-semibold text-slate-700">Diskon Tambahan PO</Label>
                  <Select value={form.po_discount_type} onValueChange={(v) => setForm({ ...form, po_discount_type: v, po_discount_value: v === "none" ? 0 : form.po_discount_value })}>
                    <SelectTrigger data-testid="po-discount-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Tanpa Diskon PO</SelectItem>
                      <SelectItem value="percent">Persen (%)</SelectItem>
                      <SelectItem value="amount">Nominal (Rp)</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input
                    type="number"
                    placeholder={form.po_discount_type === "percent" ? "misal: 5" : "misal: 100000"}
                    value={form.po_discount_value || 0}
                    disabled={form.po_discount_type === "none"}
                    onChange={(e) => setForm({ ...form, po_discount_value: Number(e.target.value) })}
                    data-testid="po-discount-value"
                  />
                </div>
                <div className="text-sm space-y-1 w-full md:w-80">
                  <div className="flex justify-between text-slate-600"><span>Subtotal (Bruto)</span><span className="font-mono">{formatIDR(grossSubtotal)}</span></div>
                  {itemDiscountTotal > 0 && <div className="flex justify-between text-red-600"><span>(-) Diskon Item</span><span className="font-mono">-{formatIDR(itemDiscountTotal)}</span></div>}
                  {poDiscount > 0 && <div className="flex justify-between text-red-600"><span>(-) Diskon PO {form.po_discount_type === "percent" ? `(${form.po_discount_value}%)` : ""}</span><span className="font-mono">-{formatIDR(poDiscount)}</span></div>}
                  <div className="flex justify-between text-slate-700 border-t pt-1"><span>Subtotal Net</span><span className="font-mono">{formatIDR(netSubtotal)}</span></div>
                  {form.include_ppn && <div className="flex justify-between text-slate-600"><span>PPN {form.ppn_percent}%</span><span className="font-mono">{formatIDR(ppn)}</span></div>}
                  <div className="flex justify-between font-display font-bold text-navy-900 pt-1 border-t"><span>Total</span><span className="font-mono" data-testid="po-total">{formatIDR(total)}</span></div>
                  {willRequireApproval && (
                    <div className="mt-2 flex items-start gap-2 rounded-md bg-yellow-50 border border-yellow-200 p-2 text-xs text-yellow-800">
                      <ShieldAlert size={14} className="flex-shrink-0 mt-0.5" />
                      <span>Nominal ≥ threshold ({formatIDR(threshold)}) — PO akan masuk antrean <strong>MENUNGGU APPROVAL</strong> Owner.</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Batal</Button>
              <Button className="bg-navy-900 hover:bg-navy-800 text-white" onClick={save} data-testid="po-save-btn">Simpan PO</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {pendingCount > 0 && isAdmin && (
        <Card className="border-yellow-300 bg-yellow-50">
          <CardContent className="p-4 flex items-center gap-3">
            <ShieldAlert className="text-yellow-700" size={24} />
            <div className="flex-1">
              <div className="font-display font-bold text-yellow-900">{pendingCount} PO menunggu approval Anda</div>
              <div className="text-sm text-yellow-800">Review dan setujui/tolak sebelum staff gudang bisa menerimanya.</div>
            </div>
          </CardContent>
        </Card>
      )}

      <Card className="card-elevated"><CardContent className="p-4">
        <div className="overflow-x-auto">
          <Table data-testid="po-table">
            <TableHeader>
              <TableRow>
                <TableHead>No. PO</TableHead><TableHead>Supplier</TableHead><TableHead>Tanggal</TableHead>
                <TableHead className="text-right">Total</TableHead><TableHead>Status</TableHead><TableHead>Approval</TableHead><TableHead className="text-right">Aksi</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {pos.map((po) => (
                <TableRow key={po.id} data-testid={`po-row-${po.po_number}`}>
                  <TableCell className="font-mono text-xs font-semibold">{po.po_number}</TableCell>
                  <TableCell>{po.supplier_name}</TableCell>
                  <TableCell>{formatDate(po.order_date)}</TableCell>
                  <TableCell className="text-right font-mono">{formatIDR(po.total)}</TableCell>
                  <TableCell><Badge className={STATUS_STYLE[po.status]}>{STATUS_LABEL[po.status]}</Badge></TableCell>
                  <TableCell className="text-xs">
                    {po.approved_by && <div className="text-emerald-700">✓ {po.approved_by} · {formatDate(po.approved_at)}</div>}
                    {po.rejected_by && <div className="text-red-700">✗ {po.rejected_by}<div className="text-slate-500 italic max-w-[200px] truncate" title={po.rejection_reason}>{po.rejection_reason}</div></div>}
                    {po.requires_approval && !po.approved_by && !po.rejected_by && <span className="text-yellow-700">Butuh approval</span>}
                    {!po.requires_approval && !po.approved_by && !po.rejected_by && <span className="text-slate-400">-</span>}
                  </TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    <Button size="sm" variant="outline" onClick={() => printPO(po)} data-testid={`po-print-${po.po_number}`}><Printer size={14} className="mr-1" />Cetak PO</Button>
                    {po.status === "pending_approval" && isAdmin && (
                      <>
                        <Button size="sm" className="ml-2 bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => approve(po.id)} data-testid={`po-approve-${po.po_number}`}><CheckCircle2 size={14} className="mr-1" />Approve</Button>
                        <Button size="sm" variant="outline" className="ml-2 border-red-300 text-red-700 hover:bg-red-50" onClick={() => setRejectPO(po)} data-testid={`po-reject-${po.po_number}`}><XCircle size={14} className="mr-1" />Reject</Button>
                      </>
                    )}
                    {po.status === "draft" && <Button size="sm" variant="outline" className="ml-2" onClick={() => setStatus(po.id, "ordered")} data-testid={`po-order-${po.po_number}`}>Order</Button>}
                    {(po.status === "ordered" || po.status === "approved") && (
                      <Button size="sm" className="ml-2 bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => receive(po.id)} data-testid={`po-receive-${po.po_number}`}>
                        <PackageCheck size={14} className="mr-1" />Terima
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {pos.length === 0 && <TableRow><TableCell colSpan={7} className="text-center py-8 text-slate-400">Belum ada PO</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      </CardContent></Card>

      {/* Reject Dialog */}
      <Dialog open={!!rejectPO} onOpenChange={(v) => !v && setRejectPO(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display">Tolak PO {rejectPO?.po_number}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="text-sm text-slate-600">Supplier: <strong>{rejectPO?.supplier_name}</strong> · Total: <strong>{formatIDR(rejectPO?.total || 0)}</strong></div>
            <div><Label>Alasan Penolakan</Label><Textarea value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} placeholder="Cth: melebihi budget bulan ini" data-testid="po-reject-reason" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setRejectPO(null); setRejectReason(""); }}>Batal</Button>
            <Button className="bg-red-600 hover:bg-red-700 text-white" onClick={submitReject} data-testid="po-reject-submit">Tolak PO</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <PdfPreviewDialog open={pdfPrev.open} url={pdfPrev.url} filename={pdfPrev.filename} title={pdfPrev.title} onClose={() => setPdfPrev({ open: false, url: "", filename: "", title: "" })} />
    </div>
  );
}
